import type { TranslationKey } from '@/types/i18n.types';

/**
 * Vocabolario dei metodi di pagamento, condiviso fra chi li scrive e chi li mostra.
 *
 * Le righe di `itinerary_installments.payment_method` hanno tre provenienze:
 *
 *   - `stripe:<tipo>` — incasso sulla piattaforma, con il tipo risolto dal charge:
 *     `stripe:card`, `stripe:customer_balance`, `stripe:sepa_debit`, `stripe:klarna`,
 *     `stripe:link`;
 *   - `stripe` — legacy, le righe scritte prima che il webhook risolvesse il tipo. Non
 *     sappiamo con cosa siano state pagate, e non e' ricostruibile: restano "Stripe";
 *   - `bank_transfer` | `cash` | `other` — registrate a mano dal TD, incassate fuori
 *     piattaforma.
 *
 * `stripe:customer_balance` e `bank_transfer` sono entrambi bonifici e vanno tenuti
 * distinti a schermo: il primo passa da Stripe (fee, payout, riconciliazione automatica),
 * il secondo e' arrivato sul conto dell'agenzia. Chiamarli allo stesso modo renderebbe la
 * dashboard inutilizzabile proprio per la domanda a cui serve rispondere.
 */

/** Metodi con cui una rata puo' essere incassata fuori piattaforma. */
export const MANUAL_PAYMENT_METHODS = ['bank_transfer', 'cash', 'other'] as const;

export type ManualPaymentMethod = (typeof MANUAL_PAYMENT_METHODS)[number];

/** Tipi Stripe con un'etichetta dedicata. Il resto ricade sul generico "Stripe". */
const STRIPE_METHOD_TYPES = ['card', 'customer_balance', 'sepa_debit', 'klarna', 'link'];

/**
 * Chiave i18n dell'etichetta di un metodo, o null se il metodo non e' noto.
 *
 * Nessun valore inatteso finisce a schermo come chiave grezza: un `stripe:<tipo>` che
 * ancora non conosciamo — Stripe ne aggiunge di continuo, e la dashboard dell'account
 * connesso decide cosa mostrare al cliente senza passare da noi — degrada su "Stripe"
 * usando il solo prefisso. Cosi' un metodo nuovo non richiede un rilascio del frontend per
 * non rompere la pagina: al massimo e' meno specifico.
 */
export const paymentMethodKey = (method: string | null | undefined): TranslationKey | null => {
    if (!method) return null;

    const [prefix, type] = method.split(':');

    if (prefix === 'stripe') {
        return (type && STRIPE_METHOD_TYPES.includes(type)
            ? `payments.methods.stripe_${type}`
            : 'payments.methods.stripe') as TranslationKey;
    }

    return ((MANUAL_PAYMENT_METHODS as readonly string[]).includes(method)
        ? `payments.methods.${method}`
        : 'payments.methods.other') as TranslationKey;
};

/**
 * Fee Place2Be su un incasso fuori piattaforma, arrotondata come la calcola il database.
 *
 * L'aliquota (frazione) non e' piu' una costante: ogni agenzia o indipendente ha la sua,
 * e arriva da `get_itinerary_platform_fee_rate`. Questa e' solo l'anteprima della
 * modale: il debito vero lo calcola `mark_installment_paid`, che la UI rilegge.
 */
export const platformFeeOn = (amount: number, rate: number): number => Math.round(amount * rate * 100) / 100;

/** Aliquota (frazione) come percentuale leggibile: 0.025 → "2,5%". */
export const formatPlatformFeeRate = (rate: number, locale = 'it-IT'): string =>
    `${(rate * 100).toLocaleString(locale, { maximumFractionDigits: 2 })}%`;
